import * as React from 'react';

import Loader from '../Components/Loader';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import StyledModal from '../Components/StyledModal';
import { CalendarDay } from '../Components/Calendar/CalendarDay';
import { CalendarEventForm } from '../Components/Calendar/CalendarEventForm';
import { CalendarEvent } from '../../MedicareProAPI/Entities/CalendarEvent';
import type { CalendarEventSchema } from '../../MedicareProAPI/Repositories/CalendarEventRepository';
import { CalendarEventRepository } from '../../MedicareProAPI/Repositories/CalendarEventRepository';
import type { CalendarEventCollection } from '../../MedicareProAPI/Collections/CalendarEventCollection';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import { CalendarWeek } from '../Components/Calendar/CalendarWeek';
import { CalendarMonth } from '../Components/Calendar/CalendarMonth';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import { AgencyRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyRepository';
import { AgencyDownlineRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import { CalendarWeekGroup } from '../Components/Calendar/CalendarWeekGroup';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import { UserContext } from '../Context/UserContext';
import { UserIntegrationProviderRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationProviderRepository';
import { ProviderCalendarEvent } from '../../MedicareProAPI/Entities/External/ProviderCalendarEvent';
import { UserIntegrationOAuthRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthRepository';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { Operator } from '../../MedicareProAPI/Model/Operator';

export interface AgentCalendarEventPanelProps {
  userAccessToken: string;
  userId: number;
  view?: CALENDAR_VIEW;
  ind?: Ind;
  user?: User;
  operator?: Operator;
}

/**
 * Types
 */
export enum CALENDAR_VIEW {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum CALENDAR_TYPE {
  DEFAULT = 'default',
  MICROSOFT = 'microsoft',
}

export type CalendarService = {
  name: string;
  type: CALENDAR_TYPE;
};

export default function AgentEventCalendarPanel(
  props: AgentCalendarEventPanelProps
): React.JSX.Element {
  const [showDownlines, setShowDownlines] = React.useState<boolean>(true);
  const [showWeekend, setShowWeekend] = React.useState<boolean>(false);
  const [calendarView, setCalendarView] = React.useState<CALENDAR_VIEW>(
    props.view ? props.view : CALENDAR_VIEW.WEEK
  );
  const [calendarType, setCalendarType] = React.useState<CALENDAR_TYPE>(
    (localStorage.getItem('calendar-type') as CALENDAR_TYPE) || CALENDAR_TYPE.DEFAULT
  );
  const [calendarUser, setCalendarUser] = React.useState<User>(null);
  const [calendarUserLoading, setCalendarUserLoading] = React.useState<boolean>(true);
  const [calendarUsers, setCalendarUsers] = React.useState<User[]>(null);
  const [calendarSelectedUsers, setCalendarSelectedUsers] = React.useState<number[]>(null);
  const [calendarUsersLoading, setCalendarUsersLoading] = React.useState<boolean>(true);
  const [calendarEvents, setCalendarEvents] = React.useState<CalendarEvent[]>(null);
  const [calendarEventsLoading, setCalendarEventsLoading] = React.useState<boolean>(true);
  const [calendarDownlineEvents, setCalendarDownlineEvents] = React.useState<CalendarEvent[]>(null);
  const [calendarDownlineEventsLoading, setCalendarDownlineEventsLoading] =
    React.useState<boolean>(true);
  const [dateTimeStart, setDateTimeStart] = React.useState<Date>(new Date());
  const [agencyId, setAgencyId] = React.useState<number>(null);

  const [userModal, setUserModal] = React.useState<boolean>(false);
  const [eventModal, setEventModal] = React.useState<boolean>(false);
  const [formCalendarEvent, setFormCalendarEvent] = React.useState<CalendarEvent>(undefined);

  const [calendarServices, setCalendarServices] = React.useState<CalendarService[]>();
  const [calendarError, setCalendarError] = React.useState<string>();

  const userData = React.useContext(UserContext);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const userIntegrationOAuthRepository = new UserIntegrationOAuthRepository();
  userIntegrationOAuthRepository.setAccessToken(props.userAccessToken);

  const calendarEventRepository = new CalendarEventRepository();
  calendarEventRepository.setAccessToken(props.userAccessToken);

  const agencyRepository = new AgencyRepository();
  agencyRepository.setAccessToken(props.userAccessToken);

  const userIntegrationProviderRepository = new UserIntegrationProviderRepository();
  userIntegrationProviderRepository.setAccessToken(props.userAccessToken);

  const agencyDownlineRepository = new AgencyDownlineRepository();
  agencyDownlineRepository.setAccessToken(props.userAccessToken);

  const userRepository = new UserRepository();
  userRepository.setAccessToken(props.userAccessToken);

  // View Buttons
  const calendarViewbuttons = [
    {
      id: CALENDAR_VIEW.DAY,
      label: 'Day',
      successClass: 'btn-success',
      icon: 'fa-clock',
    },
    {
      id: CALENDAR_VIEW.WEEK,
      label: 'Week',
      successClass: 'btn-success',
      icon: 'fa-clock',
    },
    {
      id: CALENDAR_VIEW.MONTH,
      label: 'Month',
      successClass: 'btn-success',
      icon: 'fa-clock',
    },
  ];

  /**
   * Triggered when component is loaded
   */
  React.useEffect(() => {
    // Get user
    userRepository.getUser(props.userId, { include: ['profilePic'] }).then((user) => {
      setCalendarUser(user);
      setCalendarUserLoading(false);
    });

    // Get user's integrations
    const newCalendarServices: CalendarService[] = [
      {
        name: 'Default',
        type: CALENDAR_TYPE.DEFAULT,
      },
    ];

    userIntegrationProviderRepository
      .getUserIntegrationFeatureAvailability(props.userId, 'calendar')
      .then((integrations) => {
        integrations.getItems().forEach((integration) => {
          newCalendarServices.push({
            name: integration.getName(),
            type: integration.getReference() as CALENDAR_TYPE,
          });
        });

        setCalendarServices(newCalendarServices);

        // Select the user's integrated calendar if they have one connected
        // TODO: Could perhaps be more elegant
        if (localStorage.getItem('calendar-type')) {
          console.log('calendar type:', localStorage.getItem('calendar-type'));
          const newCalendarType = localStorage.getItem('calendar-type') as CALENDAR_TYPE;
          setCalendarType(newCalendarType);
        } else if (
          newCalendarServices &&
          newCalendarServices.length > 1 &&
          newCalendarServices[newCalendarServices.length - 1]
        ) {
          console.log('calendar type:', newCalendarServices[newCalendarServices.length - 1].type);
          setCalendarType(newCalendarServices[newCalendarServices.length - 1].type);
        }
      });

    // Get user agency
    agencyRepository
      .getUserAgency(props.userId)
      .then((agency) => {
        webAPI
          .getUserIsLoaAgencyPrincipal(props.userId, agency.getAgencyId())
          .then((isAgencyPrinciple) => {
            if (isAgencyPrinciple === true) {
              agencyDownlineRepository
                .getAgencyLoasAndPrincipal(props.userId, agency.getAgencyId(), {
                  limit: 10000,
                  include: ['user.profilePic', 'offices'],
                })
                .then((agencyDownlines) => {
                  const users = Array.from(agencyDownlines.getItems(), (agencyDownline) =>
                    agencyDownline.getUser()
                  );
                  const userIndex = users.findIndex(
                    (userSingle) => userSingle.getUserId() === userData.userId
                  );

                  users.unshift(users.splice(userIndex, 1)[0]);

                  setCalendarUsers(users);
                  setCalendarSelectedUsers(Array.from(users, (user) => user.getUserId()));
                  setCalendarUsersLoading(false);
                  setAgencyId(agency.getAgencyId());
                });
            } else {
              setShowDownlines(false);
            }
          });
      })
      .catch((e) => {
        setShowDownlines(false);
        setCalendarUsersLoading(false);
        getCalendarEvents();
      });

    const showWeekend = parseInt(localStorage.getItem('calendar-showWeekend')) === 1;
    setShowWeekend(showWeekend);

    const showDownlines = parseInt(localStorage.getItem('calendar-showDownlines')) === 1;
    setShowDownlines(showDownlines);
  }, []);

  /**
   * Triggered when calendarView or dateTimeStart is updated
   */
  React.useEffect(() => {
    if (calendarType) {
      localStorage.setItem('calendar-type', calendarType);
    }

    getCalendarEvents(true);
  }, [calendarType, calendarView, dateTimeStart, agencyId, showDownlines]);

  /**
   * Get events based on current view and date. Can be used for first load and also to refresh data.
   *
   */
  const getCalendarEvents = (clearExistingEvents: boolean = false): void => {
    setCalendarEventsLoading(true);
    if (clearExistingEvents === true) {
      setCalendarEvents(null);
    }

    // Define start and end times .These will be set based on the current view.
    let start;
    let end;

    // Day view
    if (calendarView === CALENDAR_VIEW.DAY) {
      // Start of day, as defined by dateTimeStart
      start = new Date(dateTimeStart);
      start.setHours(0);
      start.setMinutes(0);

      // End of day, as defined by dateTimeStart
      end = new Date(dateTimeStart);
      end.setHours(23);
      end.setMinutes(59);
    } else if (calendarView === CALENDAR_VIEW.WEEK) {
      // Get the first day of the week, as defined by dateTimeStart
      start = Utilities.getFirstDayOfWeek(dateTimeStart);
      start.setHours(0);
      start.setMinutes(0);

      // Get the last day of the week, as defined by dateTimeStart
      end = Utilities.getLastDayOfWeek(dateTimeStart);
      end.setHours(23);
      end.setMinutes(59);
    } else if (calendarView === CALENDAR_VIEW.MONTH) {
      // Get the first day of the month, as defined by dateTimeStart
      start = Utilities.getFirstDayOfMonth(dateTimeStart);

      // Get the last day of the month, as defined by dateTimeStart
      end = Utilities.getLastDayOfMonth(dateTimeStart);
    }

    const startString = Utilities.dateTimeToDateTimeString(start);
    const endString = Utilities.dateTimeToDateTimeString(end);

    if (calendarType === CALENDAR_TYPE.MICROSOFT) {
      userIntegrationOAuthRepository
        .getUserIntegrationOAuthCalendarEvents(props.userId, calendarType, null, {
          startDateTime: startString,
          endDateTime: endString,
          include: ['user', 'user.profilePic', 'ind.addresses', 'ind.spouse'],
        })
        .then((calendarResponse) => {
          setCalendarEvents(calendarResponse.getItems());
          setCalendarEventsLoading(false);
        })
        .catch((e: Error) => {
          setCalendarError(e.message);
        });
    } else {
      calendarEventRepository
        .getCalendarEventsBetweenDates(props.userId, startString, endString, {
          include: ['user', 'user.profilePic', 'ind.addresses', 'ind.spouse'],
        })
        .then((calendarEvents: CalendarEventCollection) => {
          setCalendarEvents(calendarEvents.getItems());
          setCalendarEventsLoading(false);
        });

      if (showDownlines === true && agencyId) {
        calendarEventRepository
          .getAgencyDownlineCalendarEventsBetweenDates(
            props.userId,
            agencyId,
            startString,
            endString,
            { include: ['user', 'user.profilePic', 'ind.addresses', 'ind.spouse'] }
          )
          .then((calendarEvents: CalendarEventCollection) => {
            setCalendarDownlineEvents(calendarEvents.getItems());
            setCalendarDownlineEventsLoading(false);
          });
      }
    }
  };

  /**
   * Delete the CalendarEvent
   *
   * @param {CalendarEvent} calendarEvent Calendar even that is being deleted
   * @returns {Promise<void>} Promise resolving once the event is deleted
   */
  const formOnDelete = (calendarEvent: CalendarEvent): Promise<void> => {
    let deleteResponse;

    if (calendarEvent instanceof ProviderCalendarEvent) {
      deleteResponse = userIntegrationOAuthRepository.deleteUserIntegrationOAuthCalendarEvent(
        calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : props.userId,
        calendarType,
        calendarEvent.getExternalID()
      );
    } else {
      deleteResponse = calendarEventRepository.deleteCalendarEvent(
        calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : props.userId,
        calendarEvent.getId()
      );
    }

    return deleteResponse.then((response) => {
      getCalendarEvents();
      setFormCalendarEvent(null);
      setEventModal(false);
      return response;
    });
  };

  /**
   * Saves changes made to the CalendarEvent
   *
   * @param {CalendarEvent} calendarEvent Calendar even that is being saved or created
   * @returns {Promise<CalendarEvent>} Promise containing the updated CalendarEvent object
   */
  const formOnSave = (calendarEvent: CalendarEvent): Promise<CalendarEvent> => {
    let calendarEventResponse;

    // Microsoft
    if (
      calendarType === CALENDAR_TYPE.MICROSOFT &&
      calendarEvent instanceof ProviderCalendarEvent
    ) {
      if (calendarEvent.isAllDayEvent()) {
        calendarEvent.getDateTimeStart().setUTCHours(0);
        calendarEvent.getDateTimeStart().setUTCMinutes(0);
        calendarEvent.getDateTimeStart().setUTCSeconds(0);

        const dateEnd = new Date(calendarEvent.getDateTimeStart());
        dateEnd.setDate(dateEnd.getDate() + 1);

        calendarEvent.setDateTimeEnd(dateEnd);
      }

      const calendarEventPayload: any = {
        subject: calendarEvent.getName(),
        start: {
          dateTime: calendarEvent.getDateTimeStart().toJSON().slice(0, 19),
          timeZone: 'UTC',
        },
        end: {
          dateTime: calendarEvent.getDateTimeEnd().toJSON().slice(0, 19),
          timeZone: 'UTC',
        },
        isAllDay: calendarEvent.isAllDayEvent(),
      };

      if (!calendarEvent.getExternalID()) {
        calendarEventPayload.body = {
          content: calendarEvent.getDescription(),
          contentType: 'html',
        };

        calendarEventPayload.location = {
          displayName: calendarEvent.getLocation(),
          locationType: 'default',
        };
      }

      // Update existing event
      if (calendarEvent.getExternalID()) {
        calendarEventResponse =
          userIntegrationOAuthRepository.updateUserIntegrationOAuthCalendarEvent(
            props.userId,
            calendarType,
            calendarEvent.getExternalID(),
            calendarEventPayload
          );
      }
      // Create new Event
      else {
        calendarEventResponse =
          userIntegrationOAuthRepository.createUserIntegrationOAuthCalendarEvent(
            props.userId,
            calendarType,
            calendarEventPayload
          );
      }

      // TODO: This part doesn't feel good
      calendarEventResponse.then((updatedEvent) => {
        // If an Ind is set, add a reference to this ind
        if (calendarEvent.getInds()) {
          calendarEventRepository.putCalendarEventExternal(
            props.userId,
            updatedEvent.getExternalID(),
            {
              inds: calendarEvent.getInds(),
            }
          );
        }
        // Otherwise attempt to delete this record
        else {
          calendarEventRepository.deleteCalendarEventExternal(
            props.userId,
            updatedEvent.getExternalID()
          );
        }

        updatedEvent.setInds(calendarEvent.getInds());

        return updatedEvent;
      });
    }
    // Default
    else {
      const calendarEventPayload: CalendarEventSchema = {
        name: calendarEvent.getName(),
        inds: calendarEvent.getInds(),
        description: calendarEvent.getDescription(),
        location: calendarEvent.getLocation(),
        dateTimeStart: calendarEvent.getDateTimeStart(),
        dateTimeEnd: calendarEvent.getDateTimeEnd(),
        isAllDayEvent: calendarEvent.isAllDayEvent(),
        creator: calendarEvent.getCreator(),
      };

      if (calendarEvent.getId()) {
        calendarEventResponse = calendarEventRepository.updateCalendarEvent(
          calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : props.userId,
          calendarEvent.getId(),
          calendarEventPayload
        );
      } else {
        calendarEventResponse = calendarEventRepository.createCalendarEvent(
          calendarEvent.getUser() ? calendarEvent.getUser().getUserId() : props.userId,
          calendarEventPayload
        );
      }
    }

    return calendarEventResponse.then((calendarEvent) => {
      // Hide modal
      setEventModal(false);

      // Update selected event
      setFormCalendarEvent(calendarEvent);

      // Update events
      getCalendarEvents();

      return calendarEvent;
    });
  };

  /**
   * Move the date to the left, depending on the view; for instance, minus one day, week or month.
   *
   * @param {Date} date Date we want to modify
   */
  const handleDateLeft = (date: Date) => {
    setCalendarEvents(null);
    const newDate = new Date(date);
    if (calendarView === CALENDAR_VIEW.DAY) {
      newDate.setDate(newDate.getDate() - 1);
    } else if (calendarView === CALENDAR_VIEW.WEEK) {
      newDate.setDate(newDate.getDate() - 7);
    } else if (calendarView === CALENDAR_VIEW.MONTH) {
      newDate.setMonth(newDate.getMonth() - 1);
    }

    setDateTimeStart(newDate);
  };

  /**
   * Move the date to the right, depending on the view; for instance, plus one day, week or month.
   *
   * @param {Date} date Date we want to modify
   */
  const handleDateRight = (date: Date) => {
    setCalendarEvents(null);
    const newDate = new Date(date);
    if (calendarView === CALENDAR_VIEW.DAY) {
      newDate.setDate(newDate.getDate() + 1);
    } else if (calendarView === CALENDAR_VIEW.WEEK) {
      newDate.setDate(newDate.getDate() + 7);
    } else if (calendarView === CALENDAR_VIEW.MONTH) {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    setDateTimeStart(newDate);
  };

  /**
   * Load a CalendarEvent into the form for editing
   *
   * @param {CalendarEvent} event
   */
  const handleEditEventStart = (event: CalendarEvent) => {
    setEventModal(true);
    setFormCalendarEvent(event);
  };

  /**
   * Start creating a new event
   */
  const handleNewEventStart = (calendarType: CALENDAR_TYPE) => {
    setEventModal(true);

    let newEvent;

    // Event is an internal event
    if (calendarType === CALENDAR_TYPE.DEFAULT) {
      newEvent = new CalendarEvent();
    }
    // Event is an external event that doesn't exist in our system
    else {
      newEvent = new ProviderCalendarEvent();
    }

    if (props.operator) {
      newEvent.setCreator(props.operator);
    }

    if (props.ind) {
      newEvent.addInd(props.ind);
    }

    setFormCalendarEvent(newEvent);
  };

  /**
   * When the calendar view is changed, first clear the events before changing the view property
   * This ensures an incomplete version of the calendar doesn't render while the new events load
   *
   * @param {CALENDAR_VIEW} newCalendarView New view to show
   */
  const handleChangeCalendarView = (newCalendarView: CALENDAR_VIEW) => {
    setCalendarEvents(null);
    setCalendarView(newCalendarView);
  };

  /**
   * Depending on the calendar view the date will render differently, for instance
   * day = Tuesday 21st July 2024
   * week = 21 July - 27 July 2024
   * month = July 2024
   *
   * @param {Date} date New view to show
   * @param {CALENDAR_VIEW} format Style to render the date in
   *
   * @returns {string} String representation of the current calendar view
   */
  const renderDateMain = (date: Date, format: CALENDAR_VIEW): string => {
    if (format === CALENDAR_VIEW.DAY) {
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        weekday: 'long',
        month: 'long',
        year: 'numeric',
      });
    } else if (format === CALENDAR_VIEW.WEEK) {
      const targetMonday = Utilities.getFirstDayOfWeek(date);
      const targetSunday = Utilities.getLastDayOfWeek(date);

      return (
        targetMonday.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) +
        ' - ' +
        targetSunday.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) +
        ' ' +
        targetSunday.getFullYear()
      );
    } else if (format === CALENDAR_VIEW.MONTH) {
      return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
  };

  const getActiveUsers = (users: User[], activeUsers: number[]): User[] => {
    if (activeUsers) {
      return users.filter((user) => activeUsers.includes(user.getUserId()));
    }

    return null;
  };

  return (
    <>
      <StyledModal
        size="md"
        title="Selected Users"
        footerCloseButton={true}
        show={userModal}
        onClose={(e) => setUserModal(false)}
      >
        {calendarUsers &&
          calendarUsers.map((user) => {
            return (
              <div className="clearfix pb-1 pt-1" key={user.getUserId()}>
                <label className="switch float-left mr-1">
                  <input
                    type="checkbox"
                    data-status="off"
                    checked={
                      calendarSelectedUsers && calendarSelectedUsers.includes(user.getUserId())
                    }
                    onChange={(e) => {
                      const newCalendarSelectedUsers = calendarSelectedUsers
                        ? [...calendarSelectedUsers]
                        : [];

                      if (e.target.checked === false) {
                        const index = newCalendarSelectedUsers.indexOf(user.getUserId());
                        newCalendarSelectedUsers.splice(index, 1);
                      } else {
                        newCalendarSelectedUsers.push(user.getUserId());
                      }

                      setCalendarSelectedUsers(newCalendarSelectedUsers);
                    }}
                  />
                  <span className="slider round pt-1"></span>
                </label>

                <div className="small pt-0">{user.getUserName()}</div>
              </div>
            );
          })}
      </StyledModal>
      <StyledModal
        size="lg"
        title={
          formCalendarEvent &&
          (formCalendarEvent.getId() ||
            (formCalendarEvent instanceof ProviderCalendarEvent &&
              formCalendarEvent.getExternalID()))
            ? 'Update Event'
            : 'Create Event'
        }
        footerCloseButton={true}
        show={eventModal}
        onClose={(e) => setEventModal(false)}
      >
        <CalendarEventForm
          calendarEvent={formCalendarEvent}
          onSave={formOnSave}
          onDelete={formOnDelete}
          users={calendarUsers || undefined}
          isEditable={true}
        />
      </StyledModal>
      <div className="mb-3">
        <div className="row">
          {calendarServices && calendarServices.length > 1 && (
            <div className="col-12 pt-1 pb-2">
              <div className="small float-left pt-1 pr-2">Select Calendar Service</div>
              <select
                className="form-control form-control-sm w-auto"
                onChange={(e) => {
                  const newCalendarType = e.target.value as CALENDAR_TYPE;
                  setCalendarType(newCalendarType);
                }}
              >
                {calendarServices.map((integration) => (
                  <option
                    selected={integration.type === calendarType}
                    value={integration.type}
                    key={integration.type}
                  >
                    {integration.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-3 pt-1">
            {calendarViewbuttons.map((tab) => {
              const buttonClassList = ['btn', 'btn-sm', 'mr-2', 'calMess'];

              if (calendarView === tab.id) {
                buttonClassList.push(tab.successClass);
              } else {
                buttonClassList.push('border-secondary');
              }

              const iconClassList = ['far', tab.icon];

              return (
                <span
                  onClick={(e) => {
                    handleChangeCalendarView(tab.id);
                  }}
                  key={tab.id}
                  className={buttonClassList.join(' ')}
                >
                  <i className={iconClassList.join(' ')}></i> {tab.label}
                </span>
              );
            })}
          </div>
          <div className="col-6 text-center font-weight-bold">
            <button onClick={(e) => handleDateLeft(dateTimeStart)} className="btn mr-2 ">
              <i className="fas fa-chevron-left text-always-dark"></i>
            </button>
            {renderDateMain(dateTimeStart, calendarView)}
            <button onClick={(e) => handleDateRight(dateTimeStart)} className="btn ml-2 ">
              <i className="fas fa-chevron-right text-always-dark"></i>
            </button>
          </div>
          <div className="col-3 text-right pt-1">
            <button
              onClick={(e) => handleNewEventStart(calendarType)}
              className="btn btn-success btn-sm"
            >
              <i className="far fa-plus-circle mr-1"></i> New Event
            </button>
          </div>
          <div className="col-12">
            <div style={{ minWidth: '142px' }} className="clearfix mt-2 mr-1 d-inline-block">
              <label className="switch float-left mr-1">
                <input
                  type="checkbox"
                  data-status="off"
                  checked={showWeekend === true}
                  onChange={(e) => {
                    const newShowWeekend = !showWeekend;
                    setShowWeekend(newShowWeekend);
                    localStorage.setItem(
                      'calendar-showWeekend',
                      newShowWeekend === true ? '1' : '0'
                    );
                  }}
                />
                <span className="slider round pt-1"></span>
              </label>

              <div className="small pt-0">Show Weekend</div>
            </div>
            {calendarUsers && calendarType === CALENDAR_TYPE.DEFAULT && (
              <div style={{ minWidth: '150px' }} className="clearfix mt-2 mr-1 d-inline-block">
                <label className="switch float-left mr-1">
                  <input
                    type="checkbox"
                    data-status="off"
                    checked={showDownlines === true}
                    onChange={(e) => {
                      const newShowDownlines = !showDownlines;
                      setShowDownlines(!showDownlines);
                      localStorage.setItem(
                        'calendar-showDownlines',
                        newShowDownlines === true ? '1' : '0'
                      );
                    }}
                  />
                  <span className="slider round pt-1"></span>
                </label>

                <div className="small pt-0">Show Downlines</div>
              </div>
            )}
            {calendarUsers && showDownlines && calendarType === CALENDAR_TYPE.DEFAULT && (
              <div
                className="clearfix mt-2 mr-1 d-inline-block cp"
                onClick={(e) => setUserModal(true)}
              >
                <div className="small pt-0 border px-2 py-1 rounded text-primary">
                  <u>
                    Showing{' '}
                    {calendarSelectedUsers && calendarUsers.length === calendarSelectedUsers.length
                      ? 'all'
                      : (calendarSelectedUsers ? calendarSelectedUsers.length : 0) +
                        ' of ' +
                        calendarUsers.length}{' '}
                    users
                  </u>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={`card ${calendarView === CALENDAR_VIEW.WEEK || calendarView === CALENDAR_VIEW.DAY ? 'no-scroll' : ''} card-tall m-0 p-0`}
      >
        <div className="card-body m-0 p-0">
          {calendarError && (
            <p className="alert alert-warning mt-3 ml-3 mr-3">
              To fill your calendar with Outlook events, please disconnect and reconnect your
              account on the <a href="/agent/settingsController/index/integration">settings page</a>
              .
            </p>
          )}
          {(calendarEvents ||
            (calendarEventsLoading === false && calendarUsersLoading === false)) && (
            <>
              {calendarView === CALENDAR_VIEW.DAY && (
                <CalendarDay
                  events={calendarEvents}
                  users={
                    calendarUsers && showDownlines && calendarType === CALENDAR_TYPE.DEFAULT
                      ? getActiveUsers(calendarUsers, calendarSelectedUsers)
                      : [calendarUser]
                  }
                  dateTime={dateTimeStart}
                  onEdit={handleEditEventStart}
                />
              )}
              {calendarView === CALENDAR_VIEW.WEEK && (
                <>
                  {calendarUsers && showDownlines && calendarType === CALENDAR_TYPE.DEFAULT ? (
                    <CalendarWeekGroup
                      events={calendarDownlineEvents}
                      users={getActiveUsers(calendarUsers, calendarSelectedUsers)}
                      dateTime={dateTimeStart}
                      onEdit={handleEditEventStart}
                      showWeekend={showWeekend}
                    />
                  ) : (
                    <CalendarWeek
                      events={calendarEvents}
                      dateTime={dateTimeStart}
                      onEdit={handleEditEventStart}
                      showWeekend={showWeekend}
                    />
                  )}
                </>
              )}
              {calendarView === CALENDAR_VIEW.MONTH && (
                <CalendarMonth
                  showDownlines={showDownlines && calendarType === CALENDAR_TYPE.DEFAULT}
                  events={calendarEvents}
                  dateTime={dateTimeStart}
                  onEdit={handleEditEventStart}
                  showWeekend={showWeekend}
                  users={getActiveUsers(calendarUsers, calendarSelectedUsers)}
                />
              )}
            </>
          )}
          {!calendarError && !calendarEvents && (calendarEventsLoading || calendarUsersLoading) && (
            <Loader size="large" className="mt-4 mb-4" />
          )}
        </div>
      </div>
    </>
  );
}
